// src/utils/notificationDeepLink.ts
// Deep-links notif (push / cold start) : conserver la cible + basculer le bon business.

const STORAGE_KEY = 'nauvatek.pendingNotifDeepLink';

export type PendingNotifDeepLink = {
  /** Business cible de la notif (si connu). */
  businessId?: string;
  /** Path + query d’origine (peut contenir businessId). */
  path: string;
};

const CAPTURE_IGNORE_PREFIXES = [
  '/login',
  '/business-selection',
  '/start',
  '/pressing',
  '/ramassage',
  '/lancement',
];

export function appendBusinessIdToPath(path: string, businessId?: string | null): string {
  if (!businessId) return path;
  const qIndex = path.indexOf('?');
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const qs = qIndex >= 0 ? path.slice(qIndex + 1) : '';
  const params = new URLSearchParams(qs);
  params.set('businessId', businessId);
  const next = params.toString();
  return next ? `${pathname}?${next}` : pathname;
}

export function stripBusinessIdFromPath(path: string): string {
  const qIndex = path.indexOf('?');
  if (qIndex < 0) return path;
  const pathname = path.slice(0, qIndex);
  const params = new URLSearchParams(path.slice(qIndex + 1));
  params.delete('businessId');
  const next = params.toString();
  return next ? `${pathname}?${next}` : pathname;
}

export function readBusinessIdFromPath(path: string): string | undefined {
  const qIndex = path.indexOf('?');
  if (qIndex < 0) return undefined;
  const id = new URLSearchParams(path.slice(qIndex + 1)).get('businessId');
  return id || undefined;
}

function shouldCapturePath(pathname: string): boolean {
  if (!pathname || pathname === '/') return false;
  return !CAPTURE_IGNORE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/** Persist un deep-link en attente (session). */
export function savePendingNotifDeepLink(pending: PendingNotifDeepLink): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // ignore quota / private mode
  }
}

export function peekPendingNotifDeepLink(): PendingNotifDeepLink | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingNotifDeepLink;
    if (!parsed?.path || typeof parsed.path !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingNotifDeepLink(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function consumePendingNotifDeepLink(): PendingNotifDeepLink | null {
  const pending = peekPendingNotifDeepLink();
  clearPendingNotifDeepLink();
  return pending;
}

/**
 * À appeler au tout début du bootstrap (avant que React Router ne redirige
 * vers /business-selection et n’efface l’URL).
 */
export function captureNotificationDeepLinkFromWindow(
  loc: Pick<Location, 'pathname' | 'search'> = window.location
): PendingNotifDeepLink | null {
  const pathname = loc.pathname || '/';
  const search = loc.search || '';
  const fullPath = `${pathname}${search}`;
  const businessIdFromQuery = readBusinessIdFromPath(fullPath);

  if (!shouldCapturePath(pathname) && !businessIdFromQuery) {
    return peekPendingNotifDeepLink();
  }

  const pending: PendingNotifDeepLink = {
    businessId: businessIdFromQuery,
    path: fullPath,
  };
  savePendingNotifDeepLink(pending);
  return pending;
}

/** Cible de navigation après sélection / switch business. */
export function resolvePendingNavigationPath(pending: PendingNotifDeepLink): string {
  return stripBusinessIdFromPath(pending.path) || '/dashboard';
}
